// Phase 7 — Reports > Dashboard. Read-only KPI/chart/table overview, wired to
// the Phase-1 backend aggregation queries (getDashboardSummary, getSalesTrend,
// etc.) via ONE combined GraphQL round trip (getDashboardDataAction).
//
// UI/read-only: nothing here dispatches a mutation or touches any existing
// screen's state — it only renders data the backend already aggregates.
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { ColumnDef } from "@tanstack/react-table";
import {
  AlertTriangle,
  CreditCard,
  IndianRupee,
  Package,
  PackageX,
  Receipt,
  RotateCcw,
  ShoppingBag,
  Store,
  Users,
  Wallet,
} from "lucide-react";
import AppConstant from "../../../Globals/AppConstants";
import { getDashboardDataAction, getTopProductsAction } from "../../../Store/actions/apparelAction";
import ApparelMenu from "../../SubComponents/apparelMenu";
import { PageHeader } from "../../../shared/page-header";
import { StatCard } from "../../../shared/stat-card";
import { DataTable } from "../../../shared/data-table";
import { CsvColumn } from "../../../shared/csv-export";
import { Skeleton } from "ui/skeleton";
import { ChartCard } from "./ChartCard";
import { TrendAreaChart } from "./TrendAreaChart";
import { SalesShareDonutChart } from "./SalesShareDonutChart";
import { CategoryBarChart } from "./CategoryBarChart";

const inr = (v: any) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(v) || 0);
const num = (v: any) => new Intl.NumberFormat("en-IN").format(Number(v) || 0);

const TABLE_TABS = [
  { key: "topProducts", label: "Top Products" },
  { key: "topCustomers", label: "Top Customers" },
  { key: "recentBills", label: "Recent Bills" },
  { key: "lowStock", label: "Low Stock" },
  { key: "deadStock", label: "Dead Stock" },
  { key: "recentProducts", label: "Recently Added" },
  { key: "recentSuppliers", label: "Suppliers" },
] as const;

type TableTabKey = (typeof TABLE_TABS)[number]["key"];

const PAGE_SIZE = 10;

const DashboardScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const apparelReducerState: any = useSelector((state: any) => state.ApparelReducerState);
  const { getDashboardData, getDashboardDataLoad, getTopProductsData, getTopProductsLoad } = apparelReducerState;

  const [activeTab, setActiveTab] = React.useState<TableTabKey>("topProducts");
  const [page, setPage] = React.useState(1);
  const [topProductsField, setTopProductsField] = React.useState<"revenue" | "qty">("revenue");

  React.useEffect(() => { setPage(1); }, [activeTab]);
  React.useEffect(() => { setPage(1); }, [topProductsField]);

  React.useEffect(() => {
    dispatch(getDashboardDataAction({}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    dispatch(getTopProductsAction({ sortBy: topProductsField, sortDir: "desc" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topProductsField]);

  const summary = getDashboardData?.getDashboardSummary;
  const salesTrend = (getDashboardData?.getSalesTrend || []).map((r: any) => ({ date: r.date, value: Number(r.amount) || 0 }));
  const customerGrowth = (getDashboardData?.getCustomerGrowth || []).map((r: any) => ({ date: r.date, value: Number(r.newCustomers) || 0 }));
  const salesByCategory = (getDashboardData?.getSalesByCategory || []).map((r: any) => ({ category: r.category, amount: Number(r.amount) || 0 }));
  const categoryStock = (getDashboardData?.getCategoryStockDistribution || []).map((r: any) => ({ label: r.category, value: Number(r.stockQuantity) || 0 }));

  const topProducts = getTopProductsData?.getTopProducts ?? getDashboardData?.getTopProducts ?? [];
  const topCustomers = getDashboardData?.getTopCustomers || [];

  // CSV export rows with currency fields rounded the same way the on-screen
  // cells are (inr()'s maximumFractionDigits: 0) — the raw rows carry full
  // decimal precision, which otherwise leaks into the CSV untouched since
  // DataTable's CSV export reads the raw field, not the formatted cell.
  const topProductsCsvRows = React.useMemo(
    () => topProducts.map((r: any) => ({ ...r, revenue: Math.round(Number(r.revenue) || 0) })),
    [topProducts]
  );
  const topCustomersCsvColumns: CsvColumn[] = [
    { key: "customerName", header: "Customer" },
    { key: "mobileNumber", header: "Mobile" },
    { key: "billCount", header: "Bills" },
    { key: "totalSpend", header: "Total Spend" },
  ];
  const topCustomersCsvRows = React.useMemo(
    () => topCustomers.map((r: any) => ({ ...r, totalSpend: Math.round(Number(r.totalSpend) || 0) })),
    [topCustomers]
  );
  const recentBills = getDashboardData?.getRecentBills || [];
  const lowStock = getDashboardData?.getLowStockItems || [];
  const deadStock = getDashboardData?.getDeadStock || [];
  const recentProducts = getDashboardData?.getRecentProducts || [];
  const recentSuppliers = getDashboardData?.getRecentSuppliers || [];

  const recentBillsCsvColumns: CsvColumn[] = [
    { key: "invoiceId", header: "Invoice" },
    { key: "customerName", header: "Customer" },
    { key: "purchaseDate_fmt", header: "Date" },
    { key: "totalamount", header: "Amount" },
  ];
  const recentBillsCsvRows = React.useMemo(
    () => recentBills.map((r: any) => ({
      ...r,
      purchaseDate_fmt: r.purchaseDate ? moment(r.purchaseDate).format("DD-MMM-YYYY") : "-",
      totalamount: Math.round(Number(r.totalamount) || 0),
    })),
    [recentBills]
  );

  const lowStockCsvColumns: CsvColumn[] = [
    { key: "productName", header: "Product" },
    { key: "productId", header: AppConstant?.productId ?? "Product ID" },
    { key: "category", header: "Category" },
    { key: "availableCount", header: "Available" },
    { key: "minimumQuantity", header: "Reorder Level" },
  ];

  const deadStockCsvColumns: CsvColumn[] = [
    { key: "productName", header: "Product" },
    { key: "productId", header: AppConstant?.productId ?? "Product ID" },
    { key: "category", header: "Category" },
    { key: "mrp", header: "MRP" },
    { key: "itemCount", header: "In Stock" },
  ];
  const deadStockCsvRows = React.useMemo(
    () => deadStock.map((r: any) => ({ ...r, mrp: Math.round(Number(r.mrp) || 0) })),
    [deadStock]
  );

  const recentProductsCsvColumns: CsvColumn[] = [
    { key: "productName", header: "Product" },
    { key: "productId", header: AppConstant?.productId ?? "Product ID" },
    { key: "category", header: "Category" },
    { key: "subCategory", header: "Sub Category" },
    { key: "mrp", header: "MRP" },
    { key: "createdOn_fmt", header: "Added On" },
  ];
  const recentProductsCsvRows = React.useMemo(
    () => recentProducts.map((r: any) => ({
      ...r,
      mrp: Math.round(Number(r.mrp) || 0),
      createdOn_fmt: r.createdOn ? moment(r.createdOn).format("DD-MMM-YYYY") : "-",
    })),
    [recentProducts]
  );

  const recentSuppliersCsvColumns: CsvColumn[] = [
    { key: "name", header: "Supplier" },
    { key: "city", header: "City" },
    { key: "mobileNumber", header: "Mobile" },
    { key: "fyPurchaseAmount", header: "Purchase Amount" },
    { key: "createdOn_fmt", header: "Added On" },
  ];
  const recentSuppliersCsvRows = React.useMemo(
    () => recentSuppliers.map((r: any) => ({
      ...r,
      fyPurchaseAmount: Math.round(Number(r.fyPurchaseAmount) || 0),
      createdOn_fmt: r.createdOn ? moment(r.createdOn).format("DD-MMM-YYYY") : "-",
    })),
    [recentSuppliers]
  );

  const topProductsColumns = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      { id: "productName", header: "Product", cell: ({ row }) => row.original?.productName },
      { id: "productId", header: AppConstant?.productId, cell: ({ row }) => row.original?.productId ? (
        <button type="button" onClick={() => navigate("/inventoryManagement", { state: { auditRecordId: row.original.productDetailId, auditSearchCode: row.original.productId } })} className="text-primary hover:underline">
          {row.original.productId}
        </button>
      ) : "-" },
      { id: "category", header: "Category", cell: ({ row }) => row.original?.category ?? "-" },
      { id: "subCategory", header: "Sub Category", cell: ({ row }) => row.original?.subCategory ?? "-" },
      { id: "quantitySold", header: "Qty Sold", cell: ({ row }) => num(row.original?.quantitySold) },
      { id: "revenue", header: "Sales", cell: ({ row }) => <span className="tabular-nums">{inr(row.original?.revenue)}</span> },
    ],
    [navigate]
  );

  const topCustomersColumns = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      { id: "customerName", header: "Customer", cell: ({ row }) => row.original?.customerName },
      { id: "mobileNumber", header: "Mobile Number", cell: ({ row }) => row.original?.customerDetailId ? (
        <button type="button" onClick={() => navigate("/customer", { state: { auditRecordId: row.original.customerDetailId, auditSearchCode: row.original.mobileNumber } })} className="text-primary hover:underline">
          {row.original.mobileNumber || row.original.customerName}
        </button>
      ) : "-" },
      { id: "billCount", header: "Orders", cell: ({ row }) => num(row.original?.billCount) },
      { id: "totalSpend", header: "Sales", cell: ({ row }) => <span className="tabular-nums">{inr(row.original?.totalSpend)}</span> },
      { id: "avgOrderValue", header: "Avg Order Value", cell: ({ row }) => {
        const bills = Number(row.original?.billCount) || 0;
        const spend = Number(row.original?.totalSpend) || 0;
        return <span className="tabular-nums">{bills > 0 ? inr(spend / bills) : "-"}</span>;
      }},
    ],
    [navigate]
  );

  const recentBillsColumns = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      { id: "invoiceId", header: "Invoice", cell: ({ row }) => row.original?.invoiceId ? (
        <button type="button" onClick={() => navigate("/billing", { state: { auditRecordId: row.original.billingId, auditSearchCode: row.original.invoiceId } })} className="text-primary hover:underline">
          {row.original.invoiceId}
        </button>
      ) : "-" },
      { id: "customerName", header: "Customer", cell: ({ row }) => row.original?.customerName },
      { id: "purchaseDate", header: "Date", cell: ({ row }) => (row.original?.purchaseDate ? moment(row.original.purchaseDate).format("DD-MMM-YYYY") : "-") },
      { id: "totalamount", header: "Amount", cell: ({ row }) => <span className="tabular-nums">{inr(row.original?.totalamount)}</span> },
    ],
    [navigate]
  );

  const lowStockColumns = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      { id: "productName", header: "Product", cell: ({ row }) => row.original?.productName },
      { id: "productId", header: AppConstant?.productId, cell: ({ row }) => row.original?.productId ? (
        <button type="button" onClick={() => navigate("/inventoryManagement", { state: { auditRecordId: row.original.productDetailId, auditSearchCode: row.original.productId } })} className="text-primary hover:underline">
          {row.original.productId}
        </button>
      ) : "-" },
      { id: "category", header: "Category", cell: ({ row }) => row.original?.category ?? "-" },
      {
        id: "availableCount",
        header: "Available",
        cell: ({ row }) => {
          const available = Number(row.original?.availableCount);
          return <span className={available <= 0 ? "font-medium text-destructive" : "font-medium text-warning"}>{num(available)}</span>;
        },
      },
      { id: "minimumQuantity", header: "Reorder Level", cell: ({ row }) => num(row.original?.minimumQuantity) },
    ],
    [navigate]
  );

  const deadStockColumns = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      { id: "productName", header: "Product", cell: ({ row }) => row.original?.productName },
      { id: "productId", header: AppConstant?.productId, cell: ({ row }) => row.original?.productId ? (
        <button type="button" onClick={() => navigate("/inventoryManagement", { state: { auditRecordId: row.original.productDetailId, auditSearchCode: row.original.productId } })} className="text-primary hover:underline">
          {row.original.productId}
        </button>
      ) : "-" },
      { id: "category", header: "Category", cell: ({ row }) => row.original?.category ?? "-" },
      { id: "mrp", header: "MRP", cell: ({ row }) => <span className="tabular-nums">{inr(row.original?.mrp)}</span> },
      { id: "itemCount", header: "In Stock", cell: ({ row }) => num(row.original?.itemCount) },
    ],
    [navigate]
  );


  const recentProductsColumns = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      { id: "productName", header: "Product", cell: ({ row }) => row.original?.productName },
      { id: "productId", header: AppConstant?.productId, cell: ({ row }) => row.original?.productId ? (
        <button type="button" onClick={() => navigate("/inventoryManagement", { state: { auditRecordId: row.original.productDetailId, auditSearchCode: row.original.productId } })} className="text-primary hover:underline">
          {row.original.productId}
        </button>
      ) : "-" },
      { id: "category", header: "Category", cell: ({ row }) => row.original?.category ?? "-" },
      { id: "subCategory", header: "Sub Category", cell: ({ row }) => row.original?.subCategory ?? "-" },
      { id: "mrp", header: "MRP", cell: ({ row }) => <span className="tabular-nums">{inr(row.original?.mrp)}</span> },
      { id: "createdOn", header: "Added On", cell: ({ row }) => (row.original?.createdOn ? moment(row.original.createdOn).format("DD-MMM-YYYY") : "-") },
    ],
    [navigate]
  );

  const recentSuppliersColumns = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      { id: "name", header: "Supplier", cell: ({ row }) => row.original?.name ? (
        <button type="button" onClick={() => navigate("/vendor", { state: { auditRecordId: row.original.vendorId, auditSearchCode: row.original.name } })} className="text-primary hover:underline">
          {row.original.name}
        </button>
      ) : "-" },
      { id: "city", header: "City", cell: ({ row }) => row.original?.city ?? "-" },
      { id: "mobileNumber", header: "Mobile", cell: ({ row }) => row.original?.mobileNumber ?? "-" },
      { id: "fyPurchaseAmount", header: "Purchase Amount", cell: ({ row }) => <span className="tabular-nums">{inr(row.original?.fyPurchaseAmount)}</span> },
      { id: "createdOn", header: "Added On", cell: ({ row }) => (row.original?.createdOn ? moment(row.original.createdOn).format("DD-MMM-YYYY") : "-") },
    ],
    [navigate]
  );

  const allTabData: Record<TableTabKey, { columns: ColumnDef<any, any>[]; rows: any[] }> = {
    topProducts: { columns: topProductsColumns, rows: topProducts },
    topCustomers: { columns: topCustomersColumns, rows: topCustomers },
    recentBills: { columns: recentBillsColumns, rows: recentBills },
    lowStock: { columns: lowStockColumns, rows: lowStock },
    deadStock: { columns: deadStockColumns, rows: deadStock },
    recentProducts: { columns: recentProductsColumns, rows: recentProducts },
    recentSuppliers: { columns: recentSuppliersColumns, rows: recentSuppliers },
  };

  const activeTabData = allTabData[activeTab];
  const pagedData = activeTabData.rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="mz h-full overflow-y-auto px-6 py-4">
      <ApparelMenu selectedKey="4" />
      {/* <PageHeader title={AppConstant?.menus?.dashboard} /> */}

      <div className="mb-4 flex items-center gap-1 border-b">
        <button
          type="button"
          className="border-b-2 border-primary px-3 py-2 text-sm font-medium text-foreground"
        >
          {AppConstant.menus.dashboard}
        </button>
      </div>

      {/* KPI cards */}
      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {getDashboardDataLoad && !summary ? (
          Array.from({ length: 24 }).map((_, i) => <Skeleton key={i} className="h-24 w-full rounded-xl" />)
        ) : (
          <>
            <StatCard label="Today's Sales" value={inr(summary?.todaysSales)} icon={IndianRupee} />
            <StatCard label="Monthly Sales" value={inr(summary?.monthlySales)} icon={IndianRupee} />
            <StatCard label="Weekly Sales" value={inr(summary?.weeklySales)} icon={IndianRupee} />
            <StatCard label="Yearly Sales" value={inr(summary?.yearlySales)} icon={IndianRupee} />
            <StatCard label="Total Products" value={num(summary?.totalProducts)} icon={Package} />
            <StatCard
              label="Low Stock Items"
              value={num(summary?.lowStockCount)}
              caption={summary?.outOfStockCount > 0 ? `${num(summary?.outOfStockCount)} out of stock` : undefined}
              trend={Number(summary?.lowStockCount) > 0 ? "down" : "neutral"}
              icon={AlertTriangle}
            />
            <StatCard label="Out of Stock" value={num(summary?.outOfStockCount)} icon={PackageX} />
            <StatCard label="Inventory Value" value={inr(summary?.inventoryValue)} icon={ShoppingBag} />
            <StatCard label="Total Bills" value={num(summary?.totalBills)} caption={`Avg ${inr(summary?.averageBillValue)}`} icon={Receipt} />
            <StatCard label="Total Customers" value={num(summary?.totalCustomers)} caption={`${num(summary?.newCustomers)} new`} icon={Users} />
            <StatCard label="Active Customers" value={num(summary?.activeCustomers)} icon={Users} />
            <StatCard label="Total Suppliers" value={num(summary?.totalSuppliers)} icon={Store} />
            <StatCard label="Active Suppliers" value={num(summary?.activeSuppliers)} icon={Store} />
            <StatCard label="Today's Return" value={inr(summary?.todaysReturn)} icon={RotateCcw} />
            <StatCard label="Weekly Return" value={inr(summary?.weeklyReturn)} icon={RotateCcw} />
            <StatCard label="Monthly Return" value={inr(summary?.monthlyReturn)} icon={RotateCcw} />
            <StatCard label="Yearly Return" value={inr(summary?.yearlyReturn)} icon={RotateCcw} />
            <StatCard label="Today Credit Given" value={inr(summary?.todaysCreditGiven)} icon={CreditCard} />
            <StatCard label="Today Credit Money Recd." value={inr(summary?.todaysCreditReceived)} icon={Wallet} />
            <StatCard label="Weekly Credit Given" value={inr(summary?.weeklyCreditGiven)} icon={CreditCard} />
            <StatCard label="Weekly Credit Money Recd." value={inr(summary?.weeklyCreditReceived)} icon={Wallet} />
            <StatCard label="Monthly Credit Given" value={inr(summary?.monthlyCreditGiven)} icon={CreditCard} />
            <StatCard label="Monthly Credit Money Recd." value={inr(summary?.monthlyCreditReceived)} icon={Wallet} />
            <StatCard label="Yearly Credit Given" value={inr(summary?.yearlyCreditGiven)} icon={CreditCard} />
            <StatCard label="Yearly Credit Money Recd." value={inr(summary?.yearlyCreditReceived)} icon={Wallet} />
          </>
        )}
      </div>

      {/* Charts */}
      <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartCard title="Sales Trend" loading={getDashboardDataLoad && !getDashboardData} isEmpty={!salesTrend.length}>
          <TrendAreaChart data={salesTrend} valueFormatter={inr} />
        </ChartCard>
        <ChartCard title="Sales by Category" bodyClassName="h-auto sm:h-64" loading={getDashboardDataLoad && !getDashboardData} isEmpty={!salesByCategory.length}>
          <SalesShareDonutChart data={salesByCategory} valueFormatter={inr} />
        </ChartCard>
        <ChartCard title="Category-wise Stock Distribution" loading={getDashboardDataLoad && !getDashboardData} isEmpty={!categoryStock.length}>
          <CategoryBarChart data={categoryStock} valueFormatter={num} />
        </ChartCard>
        <ChartCard title="Customer Growth" loading={getDashboardDataLoad && !getDashboardData} isEmpty={!customerGrowth.length}>
          <TrendAreaChart data={customerGrowth} valueFormatter={num} />
        </ChartCard>
      </div>

      {/* Tables */}
      <div className="mb-2 flex flex-wrap gap-1 border-b">
        {TABLE_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={
              activeTab === tab.key
                ? "border-b-2 border-primary px-3 py-2 text-sm font-medium text-foreground"
                : "border-b-2 border-transparent px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground"
            }
          >
            {tab.label}
          </button>
        ))}
      </div>
      <DataTable
        columns={activeTabData.columns}
        data={pagedData}
        loading={
          activeTab === "topProducts" ? getTopProductsLoad
          : getDashboardDataLoad && !getDashboardData
        }
        // Same combined "Sort by" + Download CSV + density toolbar design as
        // the Custom Dashboard's Top Selling Products table.
        toolbar={
          activeTab === "topProducts" ? (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">Sort by</span>
              <div className="inline-flex rounded-full border p-0.5">
                <button
                  type="button"
                  onClick={() => setTopProductsField("qty")}
                  className={`rounded-full px-3 py-1 font-medium transition-colors ${
                    topProductsField === "qty" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  Quantity
                </button>
                <button
                  type="button"
                  onClick={() => setTopProductsField("revenue")}
                  className={`rounded-full px-3 py-1 font-medium transition-colors ${
                    topProductsField === "revenue" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  Sales
                </button>
              </div>
            </div>
          ) : undefined
        }
        csvExport={
          activeTab === "topProducts"
            ? { fileName: `top-products_${moment().format("YYYY-MM-DD")}`, rows: topProductsCsvRows }
            : activeTab === "topCustomers"
            ? { fileName: `top-customers_${moment().format("YYYY-MM-DD")}`, rows: topCustomersCsvRows, columns: topCustomersCsvColumns }
            : activeTab === "recentBills"
            ? { fileName: `recent-bills_${moment().format("YYYY-MM-DD")}`, rows: recentBillsCsvRows, columns: recentBillsCsvColumns }
            : activeTab === "lowStock"
            ? { fileName: `low-stock_${moment().format("YYYY-MM-DD")}`, rows: lowStock, columns: lowStockCsvColumns }
            : activeTab === "deadStock"
            ? { fileName: `dead-stock_${moment().format("YYYY-MM-DD")}`, rows: deadStockCsvRows, columns: deadStockCsvColumns }
            : activeTab === "recentProducts"
            ? { fileName: `recently-added_${moment().format("YYYY-MM-DD")}`, rows: recentProductsCsvRows, columns: recentProductsCsvColumns }
            : activeTab === "recentSuppliers"
            ? { fileName: `suppliers_${moment().format("YYYY-MM-DD")}`, rows: recentSuppliersCsvRows, columns: recentSuppliersCsvColumns }
            : undefined
        }
        pagination={{
          page,
          total: activeTabData.rows.length,
          pageSize: PAGE_SIZE,
          onChange: setPage,
        }}
      />
    </div>
  );
};

export default DashboardScreen;
